import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControlLabel,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { axiosInstance } from "../../../utils/networking";
import { BulkCloseResult } from "../../../interfaces";
import {
  BULK_CHUNK_SIZE,
  BulkRunError,
  REASON_MAX,
  REASON_MIN,
  WarningTotals,
  YOUNG_CASE_DAYS,
  describeSkipped,
  isValidReason,
  plural,
  runBulkClose,
} from "./caseManagementUtils";

const PRIMARY = "#9E1F63FF";
const buttonSx = { textTransform: "none", fontWeight: 600, borderRadius: 2 };

interface CloseCasesDialogProps {
  open: boolean;
  /** Ids to send — the selection minus cases known to be young. */
  caseIds: string[];
  /** Selected cases left out because they were opened < 30 days ago. */
  youngExcluded: number;
  warnings: WarningTotals;
  /** Count warnings for every selected case (pages the filter result). */
  onLoadAllWarnings?: () => void;
  loadingWarnings: boolean;
  defaultReason: string;
  onClose: () => void;
  /** A run finished (fully or partially) — refresh the page. */
  onFinished: (result: BulkCloseResult) => void;
  onForbidden: () => void;
}

type Phase = "form" | "running" | "done";

const CloseCasesDialog = ({
  open,
  caseIds,
  youngExcluded,
  warnings,
  onLoadAllWarnings,
  loadingWarnings,
  defaultReason,
  onClose,
  onFinished,
  onForbidden,
}: CloseCasesDialogProps) => {
  const [phase, setPhase] = useState<Phase>("form");
  const [reason, setReason] = useState(defaultReason);
  const [touched, setTouched] = useState(false);
  const [cancelReminders, setCancelReminders] = useState(true);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState<BulkCloseResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fresh form (and a fresh prefill) every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setPhase("form");
    setReason(defaultReason);
    setTouched(false);
    setCancelReminders(true);
    setProgress({ done: 0, total: 0 });
    setResult(null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const count = caseIds.length;
  const reasonOk = isValidReason(reason);
  const unknown = count + youngExcluded - warnings.counted;

  const submit = async () => {
    setTouched(true);
    if (!reasonOk || count === 0) return;
    // Snapshot: the page clears its selection once the run reports back.
    const ids = [...caseIds];
    setPhase("running");
    setError(null);
    setProgress({ done: 0, total: Math.ceil(ids.length / BULK_CHUNK_SIZE) });
    try {
      const res = await runBulkClose(
        ids,
        { reason, cancelReminders },
        (body) => axiosInstance.post(`/cases/bulk-close`, body).then((r) => r.data),
        (done, total) => setProgress({ done, total }),
      );
      setResult(res);
      setPhase("done");
      onFinished(res);
    } catch (err: any) {
      const cause = err instanceof BulkRunError ? (err.cause as any) : err;
      if (cause?.response?.status === 403) {
        onClose();
        onForbidden();
        return;
      }
      const message =
        cause?.response?.data?.message || cause?.message || "The server did not respond.";
      if (err instanceof BulkRunError && err.chunksDone > 0) {
        const partial = err.partial as BulkCloseResult;
        setResult(partial);
        setError(
          `Stopped after ${err.chunksDone} of ${Math.ceil(ids.length / BULK_CHUNK_SIZE)} ` +
            `chunks: ${message}. The cases already closed are in one batch you can undo ` +
            `from Recent batches.`,
        );
        setPhase("done");
        onFinished(partial);
      } else {
        setError(`Nothing was closed: ${message}`);
        setPhase("form");
      }
    }
  };

  const busy = phase === "running";

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 3 } }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>
        {phase === "done" ? "Bulk close finished" : `Close ${plural(count, "case")}?`}
      </Typography>

      {phase === "done" && result ? (
        <Stack spacing={1.5}>
          {error ? <Alert severity="warning">{error}</Alert> : null}
          <Alert severity={result.closed > 0 ? "success" : "info"}>
            Closed <b>{result.closed.toLocaleString()}</b>{" "}
            {result.closed === 1 ? "case" : "cases"}
            {result.skipped
              ? `, skipped ${result.skipped.toLocaleString()}${
                  describeSkipped(result.skippedReasons)
                    ? ` (${describeSkipped(result.skippedReasons)})`
                    : ""
                }`
              : ""}
            . {plural(result.remindersCancelled, "pending reminder")} cancelled.
          </Alert>
          {result.batch_id ? (
            <Typography variant="body2" color="text.secondary">
              Batch <code>{result.batch_id}</code> — undo it from Recent batches at the top of
              the page (cancelled reminders are not restored).
            </Typography>
          ) : null}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              disableElevation
              onClick={onClose}
              sx={{ ...buttonSx, backgroundColor: PRIMARY, "&:hover": { backgroundColor: "#7d184e" } }}
            >
              Done
            </Button>
          </Box>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Each case is marked closed with a “Case Closed” activity carrying the reason below.
            Bulk-closed cases are left out of closed-case metrics, and the whole batch can be
            undone from Recent batches.
            {youngExcluded > 0
              ? ` ${plural(youngExcluded, "selected case")} opened under ${YOUNG_CASE_DAYS} days ago ${
                  youngExcluded === 1 ? "is" : "are"
                } left out.`
              : ""}
            {unknown > 0
              ? ` Any case opened under ${YOUNG_CASE_DAYS} days ago is skipped by the server.`
              : ""}
          </Typography>

          <Box sx={{ border: "1px solid #ececf0", borderRadius: 2, px: 2, py: 1.5 }}>
            <Typography variant="caption" sx={{ color: "#8a8a93", fontWeight: 600 }}>
              OPEN WORK ON THE SELECTED CASES
            </Typography>
            {warnings.counted === 0 && unknown > 0 ? (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Not counted yet.
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {plural(warnings.pendingReminders, "pending reminder")} ·{" "}
                {plural(warnings.openReferrals, "open referral")} ·{" "}
                {plural(warnings.openTasks, "open task")}
                {warnings.casesWithWarnings
                  ? ` — on ${plural(warnings.casesWithWarnings, "case")}`
                  : ""}
              </Typography>
            )}
            {unknown > 0 ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Typography variant="body2" sx={{ color: "#8a6d00" }}>
                  Counted for {warnings.counted.toLocaleString()} of{" "}
                  {(count + youngExcluded).toLocaleString()} selected cases.
                </Typography>
                {onLoadAllWarnings ? (
                  <Button
                    size="small"
                    onClick={onLoadAllWarnings}
                    disabled={loadingWarnings || busy}
                    sx={{ ...buttonSx, color: PRIMARY }}
                    startIcon={loadingWarnings ? <CircularProgress size={14} color="inherit" /> : undefined}
                  >
                    {loadingWarnings ? "Counting…" : "Count all"}
                  </Button>
                ) : null}
              </Stack>
            ) : null}
            <Typography variant="caption" sx={{ color: "#8a8a93", display: "block", mt: 0.5 }}>
              Referrals and action-item tasks stay as they are; only reminders can be cancelled
              here.
            </Typography>
          </Box>

          <TextField
            label="Reason"
            required
            multiline
            minRows={2}
            value={reason}
            disabled={busy}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            inputProps={{ maxLength: REASON_MAX }}
            error={touched && !reasonOk}
            helperText={
              touched && !reasonOk
                ? `A reason of ${REASON_MIN}–${REASON_MAX} characters is required.`
                : `Saved on every “Case Closed” activity. ${reason.trim().length}/${REASON_MAX}`
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={cancelReminders}
                disabled={busy}
                onChange={(e) => setCancelReminders(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Also cancel pending reminders</Typography>
                <Typography variant="caption" color="text.secondary">
                  Owners who are still active get a calendar cancellation.
                </Typography>
              </Box>
            }
          />

          {error ? <Alert severity="error">{error}</Alert> : null}

          {busy ? (
            <Box>
              <LinearProgress
                variant={progress.total > 1 ? "determinate" : "indeterminate"}
                value={progress.total ? (progress.done / progress.total) * 100 : 0}
                sx={{ "& .MuiLinearProgress-bar": { backgroundColor: PRIMARY } }}
              />
              <Typography variant="caption" color="text.secondary">
                Closing… {progress.total > 1 ? `chunk ${Math.min(progress.done + 1, progress.total)} of ${progress.total}` : ""}
              </Typography>
            </Box>
          ) : null}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={busy}
              sx={{ ...buttonSx, color: "text.primary", borderColor: "divider" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              color="error"
              onClick={submit}
              disabled={busy || count === 0 || !reasonOk}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={buttonSx}
            >
              {busy ? "Closing…" : `Close ${plural(count, "case")}`}
            </Button>
          </Box>
        </Stack>
      )}
    </Dialog>
  );
};

export default CloseCasesDialog;
